import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Alert, Button, Field, Input, Logo } from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('A senha deve ter ao menos 8 caracteres.');
    if (password !== confirm) return setError('As senhas não conferem.');
    setSubmitting(true);
    try {
      await api.confirmPasswordReset({ token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      if (err instanceof PnlApiError) {
        if (err.api.code === 'INVALID_TOKEN' || err.api.code === 'EXPIRED_TOKEN')
          setError('Link inválido ou expirado. Solicite um novo.');
        else if (err.api.code === 'ALREADY_USED') setError('Este link já foi utilizado.');
        else setError(err.api.message);
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="animate-fade-in space-y-8">
      <div className="lg:hidden">
        <Logo />
      </div>
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Segurança</p>
        <h2 className="font-display text-ink-900 mt-2 text-3xl font-semibold tracking-tight">
          Definir nova senha
        </h2>
      </div>

      {!token ? (
        <Alert tone="danger">
          Link inválido. Solicite a redefinição novamente em{' '}
          <Link to="/forgot-password" className="underline">
            Esqueci minha senha
          </Link>
          .
        </Alert>
      ) : done ? (
        <Alert tone="success">Senha alterada com sucesso! Redirecionando para o login…</Alert>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label="Nova senha" htmlFor="pw" required>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              leadingIcon={<Lock size={14} />}
              placeholder="Mínimo 8 caracteres"
              disabled={submitting}
            />
          </Field>
          <Field label="Confirmar senha" htmlFor="pw2" required>
            <Input
              id="pw2"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              leadingIcon={<Lock size={14} />}
              placeholder="Repita a senha"
              disabled={submitting}
            />
          </Field>
          <Button type="submit" block size="lg" loading={submitting}>
            {submitting ? 'Salvando…' : 'Salvar nova senha'}
          </Button>
        </form>
      )}
    </div>
  );
}
